import { useEffect, useRef, useState } from 'react'
import { challengeFresh, type Prepared } from './passkey'
import { CHALLENGE_RENEW_MS } from './passkey-rules'

/** After a failed fetch, try again on its own this many times (a flaky phone connection), then wait for a tap. */
const RETRY_DELAYS_MS = [3_000, 10_000, 30_000]

/**
 * Keeps a passkey challenge ready for the next tap (the sign-in gate, the setup panel and the confirm step share
 * it). It is fetched when `enabled`, renewed every minute, and renewed again when the page comes back into view,
 * since a phone that locked (or a background tab) never ran the timer. A failed fetch retries on its own a few times
 * and then reports `failed`, so the screen can offer "Try again" instead of a button stuck on "Getting ready".
 *
 * `take()` hands over the challenge only if it is fresh and no prompt is already open; otherwise it returns null (and
 * renews a stale one), so the caller asks for one more tap instead of letting the device prompt succeed and the
 * server then refuse it. `release()` reopens taps after a prompt settles.
 */
export function usePreparedChallenge(load: () => Promise<Prepared>, enabled: boolean, onError: (e: unknown) => void) {
  const [prepared, setPrepared] = useState<Prepared | null>(null)
  const [failed, setFailed] = useState(false)
  const [round, setRound] = useState(0)
  const loadRef = useRef(load)
  loadRef.current = load
  const errRef = useRef(onError)
  errRef.current = onError
  const current = useRef<Prepared | null>(null)
  const pending = useRef(false) // a device prompt is open

  useEffect(() => {
    if (!enabled) return
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined
    let attempt = 0
    const run = async () => {
      try {
        const p = await loadRef.current()
        if (!live) return
        attempt = 0
        current.current = p
        setPrepared(p)
        setFailed(false)
        timer = setTimeout(() => void run(), CHALLENGE_RENEW_MS)
      } catch (e) {
        if (!live) return
        errRef.current(e)
        const delay = RETRY_DELAYS_MS[attempt++]
        if (delay === undefined) setFailed(true)
        else timer = setTimeout(() => void run(), delay)
      }
    }
    const onShow = () => {
      const p = current.current
      if (document.visibilityState === 'visible' && p && !challengeFresh(p.fetchedAt, Date.now())) setRound((n) => n + 1)
    }
    current.current = null
    setPrepared(null)
    setFailed(false)
    void run()
    document.addEventListener('visibilitychange', onShow)
    window.addEventListener('pageshow', onShow)
    return () => {
      live = false
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onShow)
      window.removeEventListener('pageshow', onShow)
    }
  }, [enabled, round])

  const renew = () => setRound((n) => n + 1)
  /** The challenge, if it is good for a tap right now; otherwise null (a stale one starts renewing). */
  const take = (): Prepared | null => {
    if (pending.current) return null
    const p = current.current
    if (p && challengeFresh(p.fetchedAt, Date.now())) {
      pending.current = true
      return p
    }
    if (p) renew()
    return null
  }
  const release = () => {
    pending.current = false
  }
  return { prepared, failed, renew, take, release, busy: () => pending.current }
}
